import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "q-ma-sm"
}
const _hoisted_2 = {
  class: "text-uppercase",
  lang: "ja"
}
const _hoisted_3 = { class: "q-gutter-xs" }

import type { PropType } from 'vue';
import { toRefs, computed, ref } from 'vue';

// ViewModel
import { useGlobalVM } from '@viewmodel/global.vm';

// Personal components
import DialogVideoView from '@components/dialog/program/DialogVideoView.vue';
import DiagGeneric from '@components/dialog/DiagGeneric.vue';

// Personal libs
import { IProgram, IVideo } from '@shared/interface/model.interface';
import { VideoDto } from '@data/dto/video-dto';
import { $t } from 'src/i18n/utils';
import { UpdateManagerProgram } from '@shared/service/updateManager/update-manager-program.service';
import {
  displayDialogError,
  displayDialogSuccess,
  displayDialogWarning,
  displayNotifSuccess,
} from '@shared/lib/notify.lib';
import { tailleFichier } from '@shared/lib/tools.lib';
import { formatDateToLitteral } from '@shared/lib/date.lib';
import videoRepository from '@data/repository/video.repository';
import { IApiResponse } from '@shared/interface/api-response.interface';
import { RepositoryStrategyEnum } from '@shared/enum/repository-strategy.enum';
import { VideoStatusEnum } from '@shared/enum/video-status.enum';
import { IDiagGenericResponse } from '@shared/interface/qDialogTemplate.interface';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';
import { InputFiedTypeEnum } from '@shared/enum/input.enum';

/**
 * Properties
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'QAdminVideoList',
  props: {
  title: { type: String, required: true },
  status: {
    type: Number as PropType<VideoStatusEnum>,
    required: true,
  },
  canValidateVideo: { type: Boolean, required: true },
  canDeleteVideo: { type: Boolean, required: true },
  programs: { type: Object as PropType<Array<IProgram>>, required: true },
},
  emits: ['loading', 'loaded'],
  setup(__props, { emit: __emit }) {

// Vendor
const props = __props;
const { title, status, programs, canValidateVideo, canDeleteVideo } =
  toRefs(props);

/**
 * Viewmodel
 */
const { isMobile } = useGlobalVM();

/**
 * Events
 */
const emits = __emit;

/**
 * Reactive datas
 */
const showDialogVideoView = ref(false);
const showDialogValidateVideo = ref(false);
const showDialogInvalidateVideo = ref(false);
const showDialogDeleteVideo = ref(false);
const showDialogAskToValidateVideo = ref(false);
const selectedVideo = ref<IVideo>(new VideoDto());
const diagGenericMessageValidate = ref('');
const diagGenericMessageInvalidate = ref('');
const diagGenericMessageDelete = ref('');
const diagGenericMessageAskToValidate = ref('');
const programLabel = ref<string>('');

/**
 * Computed
 */
const isOnline = computed(() => sessionManager.isOnline.value === true);
const hasToDisable = computed<boolean>(
  () => !isOnline.value,
);
const nbVideos = computed<number>(() =>
  programs.value.reduce(
    (prev, current) => prev + (current.videos ?? []).length,
    0,
  ),
);

/**
 * Methods
 */
function videoCreatedAt(video: IVideo): string {
  if (video.createdAt != null) {
    return formatDateToLitteral(video.createdAt);
  } else {
    return 'date non définie';
  }
}

function videoValidatedAt(video: IVideo): string | undefined {
  if (video.validatedAt != null) {
    return formatDateToLitteral(video.validatedAt);
  } else {
    return undefined;
  }
}

function showValidateVideo(video: IVideo) {
  selectedVideo.value = video;

  diagGenericMessageValidate.value = `Validation de la vidéo '${
    video.fileName
  }' créée par '${video.creator?.username || 'inconnu'}' ?`;

  showDialogValidateVideo.value = true;
}

function showInvalidateVideo(video: IVideo) {
  selectedVideo.value = video;

  diagGenericMessageInvalidate.value = `Invalidation de la vidéo '${
    video.fileName
  }' créée par '${video.creator?.username || 'inconnu'}' ?`;

  showDialogInvalidateVideo.value = true;
}

function showDeleteVideo(video: IVideo) {
  selectedVideo.value = video;
  diagGenericMessageDelete.value = `Suppression de la vidéo '${
    video.fileName
  }' créée par '${video.creator?.username || 'inconnu'}' ?`;

  showDialogDeleteVideo.value = true;
}

function showAskToValidateVideo(video: IVideo) {
  selectedVideo.value = video;
  diagGenericMessageAskToValidate.value = `Demander la validation de la vidéo '${video.fileName}' ?`;

  showDialogAskToValidateVideo.value = true;
}

async function onValidateVideo() {
  await videoRepository
    .validateVideo(selectedVideo.value.id)
    .then((response: IApiResponse<string, string> | undefined) => {
      emits('loading');

      new UpdateManagerProgram.ProgramAll()
        .exec(RepositoryStrategyEnum.NETWORK_FIRST)
        .finally(() => {
          if (response?.returnCode === 'VIDEO_VALIDATE_OK') {
            displayNotifSuccess('Vidéo validée avec succès');
          } else if (response?.returnCode === 'VIDEO_VALIDATE_OK_NO_MAIL') {
            displayDialogWarning({
              message:
                "Vidéo validée avec succès (mais erreur technique lors de l'envoi de mail au propriétaire de la vidéo)",
            });
          } else {
            displayDialogError({
              message: 'Erreur lors de la validation de la vidéo',
            });
          }
          emits('loaded');
        });
    })
    .catch((error) => displayDialogError({ message: error.message ?? 'null' }))
    .finally(() => {
      showDialogValidateVideo.value = false;
    });
}

async function onInvalidateVideo(dialogReturn?: IDiagGenericResponse) {
  await videoRepository
    .invalidateVideo({
      videoId: selectedVideo.value.id,
      invalidationReason: dialogReturn?.inputTextarea,
    })
    .then((response: IApiResponse<string, string> | undefined) => {
      emits('loading');

      new UpdateManagerProgram.ProgramAll()
        .exec(RepositoryStrategyEnum.NETWORK_FIRST)
        .finally(() => {
          if (response?.returnCode === 'VIDEO_INVALIDATE_OK') {
            displayNotifSuccess('Vidéo invalidée avec succès');
          } else if (response?.returnCode === 'VIDEO_INVALIDATE_OK_NO_MAIL') {
            displayDialogSuccess({
              message:
                "Vidéo invalidée avec succès (mais erreur technique lors de l'envoi de mail au propriétaire de la vidéo)",
            });
          } else {
            displayDialogError({
              message: "Erreur lors de l'invalidation de la vidéo",
            });
          }
          emits('loaded');
        });
    })
    .catch((error) => displayDialogError({ message: error.message ?? 'null' }))
    .finally(() => (showDialogInvalidateVideo.value = false));
}

async function onAskToValidateVideo() {
  await videoRepository
    .askToValidateVideo(selectedVideo.value.id)
    .then(() => {
      emits('loading');
      new UpdateManagerProgram.ProgramAll()
        .exec(RepositoryStrategyEnum.NETWORK_FIRST)
        .finally(() => {
          displayNotifSuccess('Demande envoyée avec succès');
          emits('loaded');
        });
    })
    .catch((error) => displayDialogError({ message: error.message ?? 'null' }))
    .finally(() => (showDialogAskToValidateVideo.value = false));
}

async function onDeleteVideo() {
  await videoRepository
    .deleteVideo(selectedVideo.value.id)
    .then(() => {
      emits('loading');

      new UpdateManagerProgram.ProgramAll()
        .exec(RepositoryStrategyEnum.NETWORK_FIRST)
        .finally(() => {
          displayNotifSuccess('Vidéo supprimée avec succès');
          emits('loaded');
        });
    })
    .catch((error) => {
      displayDialogError({ message: error.message ?? 'null' });
      new UpdateManagerProgram.ProgramAll().exec(
        RepositoryStrategyEnum.NETWORK_FIRST,
      );
    })
    .finally(() => (showDialogDeleteVideo.value = false));
}

function showVideo(video: IVideo, _programLabel: string) {
  selectedVideo.value = video;
  programLabel.value = _programLabel;
  showDialogVideoView.value = true;
}

function defineBgColor(program?: IProgram): string {
  if (program != null) {
    return `belt-${program.beltCode}-${program.gradeLevel}`;
  } else {
    return 'belt-yellow-1';
  }
}

return (_ctx: any,_cache: any) => {
  const _component_q_item_label = _resolveComponent("q-item-label")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_item_section = _resolveComponent("q-item-section")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_item = _resolveComponent("q-item")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_menu = _resolveComponent("q-menu")!
  const _component_q_expansion_item = _resolveComponent("q-expansion-item")!
  const _component_q_list = _resolveComponent("q-list")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_q_list, {
      bordered: "",
      class: "rounded-borders q-ma-xs",
      role: "list"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_q_expansion_item, {
          "expand-separator": "",
          label: `${_unref(title)} (${nbVideos.value})`,
          "header-class": "bg-grey-5 text-black text-uppercase",
          "expand-icon-class": "text-black"
        }, {
          default: _withCtx(() => [
            (_unref(programs).length === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_1, "Aucune vidéo"))
              : _createCommentVNode("v-if", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(programs), (program, index) => {
              return (_openBlock(), _createElementBlock(_Fragment, { key: index }, [
                _createVNode(_component_q_item_label, {
                  class: _normalizeClass(['q-pa-sm text-weight-medium', defineBgColor(program)])
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("span", _hoisted_2, _toDisplayString(program.label), 1 /* TEXT */),
                    _createTextVNode(" (ceinture " + _toDisplayString(_unref($t)(`generic.beltLabel.${program?.beltCode || 'yellow'}`)) + ") ", 1 /* TEXT */)
                  ]),
                  _: 2 /* DYNAMIC */
                }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["class"]),
                _createVNode(_component_q_separator),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(program.videos, (video, indexVideo) => {
                  return (_openBlock(), _createElementBlock(_Fragment, {
                    key: String(index) + String(indexVideo)
                  }, [
                    _createVNode(_component_q_item, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_q_item_section, { top: "" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_q_item_label, { lines: "1" }, {
                              default: _withCtx(() => [
                                _createTextVNode(" Taille : " + _toDisplayString(_unref(tailleFichier)(video.fileSize)), 1 /* TEXT */)
                              ]),
                              _: 2 /* DYNAMIC */
                            }, 1024 /* DYNAMIC_SLOTS */),
                            _createVNode(_component_q_item_label, { lines: "1" }, {
                              default: _withCtx(() => [
                                _createTextVNode(" Dépôt : " + _toDisplayString(videoCreatedAt(video)), 1 /* TEXT */)
                              ]),
                              _: 2 /* DYNAMIC */
                            }, 1024 /* DYNAMIC_SLOTS */),
                            (
                  videoValidatedAt(video) != null &&
                  video.status == _unref(VideoStatusEnum).VALIDATED
                )
                              ? (_openBlock(), _createBlock(_component_q_item_label, {
                                  key: 0,
                                  lines: "1"
                                }, {
                                  default: _withCtx(() => [
                                    _createTextVNode(" Validée : " + _toDisplayString(videoValidatedAt(video)), 1 /* TEXT */)
                                  ]),
                                  _: 2 /* DYNAMIC */
                                }, 1024 /* DYNAMIC_SLOTS */))
                              : _createCommentVNode("v-if", true),
                            _createVNode(_component_q_item_label, { lines: "1" }, {
                              default: _withCtx(() => [
                                _createTextVNode(" Créateur : " + _toDisplayString(video.creator?.username || 'inconnu') + " (" + _toDisplayString(video.creator?.email || 'inconnu') + ") ", 1 /* TEXT */)
                              ]),
                              _: 2 /* DYNAMIC */
                            }, 1024 /* DYNAMIC_SLOTS */)
                          ]),
                          _: 2 /* DYNAMIC */
                        }, 1024 /* DYNAMIC_SLOTS */),
                        _createVNode(_component_q_item_section, { side: "" }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_3, [
                              _createCommentVNode(" Desktop version "),
                              (!_unref(isMobile))
                                ? (_openBlock(), _createBlock(_component_q_btn, {
                                    key: 0,
                                    size: "12px",
                                    flat: "",
                                    dense: "",
                                    round: "",
                                    class: "set-outline",
                                    icon: "visibility",
                                    onClick: _withModifiers(($event: any) => (showVideo(video, program.label ?? '')), ["stop"]),
                                    title: `Visualiser la vidéo '${video.fileName}'`
                                  }, null, 8 /* PROPS */, ["onClick", "title"]))
                                : _createCommentVNode("v-if", true),
                              (
                    !_unref(isMobile) &&
                    _unref(canValidateVideo) &&
                    _unref(status) != _unref(VideoStatusEnum).VALIDATED &&
                    _unref(status) != _unref(VideoStatusEnum).DRAFT
                  )
                                ? (_openBlock(), _createBlock(_component_q_btn, {
                                    key: 1,
                                    size: "12px",
                                    flat: "",
                                    dense: "",
                                    round: "",
                                    class: "set-outline",
                                    icon: "check",
                                    disable: hasToDisable.value,
                                    onClick: _withModifiers(($event: any) => (showValidateVideo(video)), ["stop"]),
                                    title: `Valider la vidéo '${video.fileName}'`
                                  }, null, 8 /* PROPS */, ["disable", "onClick", "title"]))
                                : _createCommentVNode("v-if", true),
                              (
                    !_unref(isMobile) &&
                    _unref(canValidateVideo) &&
                    _unref(status) != _unref(VideoStatusEnum).DRAFT
                  )
                                ? (_openBlock(), _createBlock(_component_q_btn, {
                                    key: 2,
                                    size: "12px",
                                    flat: "",
                                    dense: "",
                                    round: "",
                                    class: "set-outline",
                                    icon: "close",
                                    disable: hasToDisable.value,
                                    onClick: _withModifiers(($event: any) => (showInvalidateVideo(video)), ["stop"]),
                                    title: `Invalider la vidéo '${video.fileName}'`
                                  }, null, 8 /* PROPS */, ["disable", "onClick", "title"]))
                                : _createCommentVNode("v-if", true),
                              (!_unref(isMobile) && _unref(status) == _unref(VideoStatusEnum).DRAFT)
                                ? (_openBlock(), _createBlock(_component_q_btn, {
                                    key: 3,
                                    size: "12px",
                                    flat: "",
                                    dense: "",
                                    round: "",
                                    class: "set-outline",
                                    icon: "arrow_upward",
                                    disable: hasToDisable.value,
                                    onClick: _withModifiers(($event: any) => (showAskToValidateVideo(video)), ["stop"]),
                                    title: `Demander la validation de la vidéo '${video.fileName}'`
                                  }, null, 8 /* PROPS */, ["disable", "onClick", "title"]))
                                : _createCommentVNode("v-if", true),
                              (!_unref(isMobile) && _unref(canDeleteVideo))
                                ? (_openBlock(), _createBlock(_component_q_btn, {
                                    key: 4,
                                    size: "12px",
                                    flat: "",
                                    dense: "",
                                    round: "",
                                    class: "set-outline",
                                    icon: "delete",
                                    disable: hasToDisable.value,
                                    onClick: _withModifiers(($event: any) => (showDeleteVideo(video)), ["stop"]),
                                    title: `Supprimer la vidéo '${video.fileName}'`
                                  }, null, 8 /* PROPS */, ["disable", "onClick", "title"]))
                                : _createCommentVNode("v-if", true),
                              _createCommentVNode(" Mobile version "),
                              (_unref(isMobile))
                                ? (_openBlock(), _createBlock(_component_q_btn, {
                                    key: 5,
                                    size: "12px",
                                    flat: "",
                                    dense: "",
                                    round: "",
                                    icon: "more_vert",
                                    onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_component_q_menu, {
                                        anchor: "center right",
                                        self: "bottom right",
                                        "auto-close": ""
                                      }, {
                                        default: _withCtx(() => [
                                          _createCommentVNode(" MENU TITLE "),
                                          _createVNode(_component_q_item, null, {
                                            default: _withCtx(() => [
                                              _createVNode(_component_q_item_section, { avatar: "" }, {
                                                default: _withCtx(() => [
                                                  _createElementVNode("span", null, "Vidéo '" + _toDisplayString(video.fileName) + "'", 1 /* TEXT */)
                                                ]),
                                                _: 2 /* DYNAMIC */
                                              }, 1024 /* DYNAMIC_SLOTS */)
                                            ]),
                                            _: 2 /* DYNAMIC */
                                          }, 1024 /* DYNAMIC_SLOTS */),
                                          _createCommentVNode(" SEE "),
                                          _createVNode(_component_q_item, { clickable: "" }, {
                                            default: _withCtx(() => [
                                              _createVNode(_component_q_item_section, { avatar: "" }, {
                                                default: _withCtx(() => [
                                                  _createVNode(_component_q_icon, { name: "visibility" })
                                                ]),
                                                _: 1 /* STABLE */
                                              }),
                                              _createVNode(_component_q_item_section, {
                                                onClick: ($event: any) => (showVideo(video, program.label ?? '')),
                                                title: `Visualiser la vidéo '${video.fileName}'`
                                              }, {
                                                default: _withCtx(() => _cache[13] || (_cache[13] = [
                                                  _createTextVNode(" Visualiser ")
                                                ])),
                                                _: 2 /* DYNAMIC */
                                              }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["onClick", "title"])
                                            ]),
                                            _: 2 /* DYNAMIC */
                                          }, 1024 /* DYNAMIC_SLOTS */),
                                          _createCommentVNode(" VALIDATE "),
                                          (
                        _unref(canValidateVideo) &&
                        _unref(status) != _unref(VideoStatusEnum).VALIDATED &&
                        _unref(status) != _unref(VideoStatusEnum).DRAFT
                      )
                                            ? (_openBlock(), _createBlock(_component_q_item, {
                                                key: 0,
                                                clickable: "",
                                                disable: hasToDisable.value
                                              }, {
                                                default: _withCtx(() => [
                                                  _createVNode(_component_q_item_section, { avatar: "" }, {
                                                    default: _withCtx(() => [
                                                      _createVNode(_component_q_icon, { name: "check" })
                                                    ]),
                                                    _: 1 /* STABLE */
                                                  }),
                                                  _createVNode(_component_q_item_section, {
                                                    onClick: ($event: any) => (
                          !hasToDisable.value ? showValidateVideo(video) : undefined
                        ),
                                                    title: `Valider la vidéo '${video.fileName}'`
                                                  }, {
                                                    default: _withCtx(() => _cache[14] || (_cache[14] = [
                                                      _createTextVNode(" Valider ")
                                                    ])),
                                                    _: 2 /* DYNAMIC */
                                                  }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["onClick", "title"])
                                                ]),
                                                _: 2 /* DYNAMIC */
                                              }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["disable"]))
                                            : _createCommentVNode("v-if", true),
                                          _createCommentVNode(" INVALIDATE "),
                                          (_unref(canValidateVideo) && _unref(status) != _unref(VideoStatusEnum).DRAFT)
                                            ? (_openBlock(), _createBlock(_component_q_item, {
                                                key: 1,
                                                clickable: "",
                                                disable: hasToDisable.value
                                              }, {
                                                default: _withCtx(() => [
                                                  _createVNode(_component_q_item_section, { avatar: "" }, {
                                                    default: _withCtx(() => [
                                                      _createVNode(_component_q_icon, { name: "close" })
                                                    ]),
                                                    _: 1 /* STABLE */
                                                  }),
                                                  _createVNode(_component_q_item_section, {
                                                    onClick: ($event: any) => (
                          !hasToDisable.value ? showInvalidateVideo(video) : undefined
                        ),
                                                    title: `Invalider la vidéo '${video.fileName}'`
                                                  }, {
                                                    default: _withCtx(() => _cache[15] || (_cache[15] = [
                                                      _createTextVNode(" Invalider ")
                                                    ])),
                                                    _: 2 /* DYNAMIC */
                                                  }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["onClick", "title"])
                                                ]),
                                                _: 2 /* DYNAMIC */
                                              }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["disable"]))
                                            : _createCommentVNode("v-if", true),
                                          _createCommentVNode(" ASK TO VALIDATE "),
                                          (_unref(status) == _unref(VideoStatusEnum).DRAFT)
                                            ? (_openBlock(), _createBlock(_component_q_item, {
                                                key: 2,
                                                clickable: "",
                                                disable: hasToDisable.value
                                              }, {
                                                default: _withCtx(() => [
                                                  _createVNode(_component_q_item_section, { avatar: "" }, {
                                                    default: _withCtx(() => [
                                                      _createVNode(_component_q_icon, { name: "arrow_upward" })
                                                    ]),
                                                    _: 1 /* STABLE */
                                                  }),
                                                  _createVNode(_component_q_item_section, {
                                                    onClick: ($event: any) => (
                          !hasToDisable.value
                            ? showAskToValidateVideo(video)
                            : undefined
                        ),
                                                    title: `Demander la validation de la vidéo '${video.fileName}'`
                                                  }, {
                                                    default: _withCtx(() => _cache[16] || (_cache[16] = [
                                                      _createTextVNode(" Faire valider ")
                                                    ])),
                                                    _: 2 /* DYNAMIC */
                                                  }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["onClick", "title"])
                                                ]),
                                                _: 2 /* DYNAMIC */
                                              }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["disable"]))
                                            : _createCommentVNode("v-if", true),
                                          _createCommentVNode(" DELETE "),
                                          (_unref(canDeleteVideo))
                                            ? (_openBlock(), _createBlock(_component_q_item, {
                                                key: 3,
                                                clickable: "",
                                                disable: hasToDisable.value
                                              }, {
                                                default: _withCtx(() => [
                                                  _createVNode(_component_q_item_section, { avatar: "" }, {
                                                    default: _withCtx(() => [
                                                      _createVNode(_component_q_icon, { name: "delete" })
                                                    ]),
                                                    _: 1 /* STABLE */
                                                  }),
                                                  _createVNode(_component_q_item_section, {
                                                    onClick: ($event: any) => (
                          !hasToDisable.value ? showDeleteVideo(video) : undefined
                        ),
                                                    title: `Supprimer la vidéo '${video.fileName}'`
                                                  }, {
                                                    default: _withCtx(() => _cache[17] || (_cache[17] = [
                                                      _createTextVNode(" Supprimer ")
                                                    ])),
                                                    _: 2 /* DYNAMIC */
                                                  }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["onClick", "title"])
                                                ]),
                                                _: 2 /* DYNAMIC */
                                              }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["disable"]))
                                            : _createCommentVNode("v-if", true)
                                        ]),
                                        _: 2 /* DYNAMIC */
                                      }, 1024 /* DYNAMIC_SLOTS */)
                                    ]),
                                    _: 2 /* DYNAMIC */
                                  }, 1024 /* DYNAMIC_SLOTS */))
                                : _createCommentVNode("v-if", true)
                            ])
                          ]),
                          _: 2 /* DYNAMIC */
                        }, 1024 /* DYNAMIC_SLOTS */)
                      ]),
                      _: 2 /* DYNAMIC */
                    }, 1024 /* DYNAMIC_SLOTS */),
                    (
              program.videos != null && indexVideo < program.videos.length - 1
            )
                      ? (_openBlock(), _createBlock(_component_q_separator, {
                          key: 0,
                          spaced: ""
                        }))
                      : _createCommentVNode("v-if", true)
                  ], 64 /* STABLE_FRAGMENT */))
                }), 128 /* KEYED_FRAGMENT */))
              ], 64 /* STABLE_FRAGMENT */))
            }), 128 /* KEYED_FRAGMENT */))
          ]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["label"])
      ]),
      _: 1 /* STABLE */
    }),
    _createVNode(DialogVideoView, {
      show: showDialogVideoView.value,
      video: selectedVideo.value,
      "program-label": programLabel.value,
      onClose: _cache[1] || (_cache[1] = ($event: any) => (showDialogVideoView.value = false))
    }, null, 8 /* PROPS */, ["show", "video", "program-label"]),
    _createVNode(DiagGeneric, {
      show: showDialogValidateVideo.value,
      title: 'Valider',
      message: diagGenericMessageValidate.value,
      "manage-loading": true,
      onClose: _cache[2] || (_cache[2] = ($event: any) => (showDialogValidateVideo.value = false)),
      onCancel: _cache[3] || (_cache[3] = ($event: any) => (showDialogValidateVideo.value = false)),
      onValid: _cache[4] || (_cache[4] = ($event: any) => (onValidateVideo()))
    }, null, 8 /* PROPS */, ["show", "message"]),
    _createVNode(DiagGeneric, {
      show: showDialogInvalidateVideo.value,
      title: 'Invalider',
      message: diagGenericMessageInvalidate.value,
      "manage-loading": true,
      "input-prompt": {
      model: '',
      type: _unref(InputFiedTypeEnum).TEXTAREA,
      label: 'Raison de l\'invalidation',
      required: true
    },
      onClose: _cache[5] || (_cache[5] = ($event: any) => (showDialogInvalidateVideo.value = false)),
      onCancel: _cache[6] || (_cache[6] = ($event: any) => (showDialogInvalidateVideo.value = false)),
      onValid: onInvalidateVideo
    }, null, 8 /* PROPS */, ["show", "message", "input-prompt"]),
    _createVNode(DiagGeneric, {
      show: showDialogDeleteVideo.value,
      title: 'Supprimer',
      message: diagGenericMessageDelete.value,
      "manage-loading": true,
      onClose: _cache[7] || (_cache[7] = ($event: any) => (showDialogDeleteVideo.value = false)),
      onCancel: _cache[8] || (_cache[8] = ($event: any) => (showDialogDeleteVideo.value = false)),
      onValid: _cache[9] || (_cache[9] = ($event: any) => (onDeleteVideo()))
    }, null, 8 /* PROPS */, ["show", "message"]),
    _createVNode(DiagGeneric, {
      show: showDialogAskToValidateVideo.value,
      title: 'Demander la validation',
      message: diagGenericMessageAskToValidate.value,
      "manage-loading": true,
      ok: { label: 'Demander' },
      onClose: _cache[10] || (_cache[10] = ($event: any) => (showDialogAskToValidateVideo.value = false)),
      onCancel: _cache[11] || (_cache[11] = ($event: any) => (showDialogAskToValidateVideo.value = false)),
      onValid: _cache[12] || (_cache[12] = ($event: any) => (onAskToValidateVideo()))
    }, null, 8 /* PROPS */, ["show", "message"])
  ], 64 /* STABLE_FRAGMENT */))
}
}

})